import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getSessionID, isLoggedIn } from 'ducks/auth';
import * as chat from 'ducks/chat';
import config from 'config';

const RECONNECT_STEP_MS = 3000;
const RECONNECT_MAX_MS = 30000;
const WS_PATH = '/api/common/chat/listen';

const buildWebSocketURL = () => {
  const base = config.serverURL
    ? config.serverURL.replace(/^http(s?)/, 'ws$1')
    : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
  return `${base}${WS_PATH}`;
};

export const useChatSocket = (enabled = true) => {
  const {
    requestChannels,
    newMessageReceived,
    messageReadReceived,
    messageEditedReceived,
    messageDeletedReceived,
  } = bindActionCreators(chat.actions, useDispatch());
  const sessionID = useSelector(getSessionID);
  const loggedIn = useSelector(isLoggedIn);
  const hasConnectedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !loggedIn || !sessionID) return undefined;

    let cancelled = false;
    let ws = null;
    let reconnectTimer = null;
    let reconnectDelay = RECONNECT_STEP_MS;
    hasConnectedRef.current = false;

    const scheduleReconnect = () => {
      if (cancelled || reconnectDelay > RECONNECT_MAX_MS) return;
      reconnectTimer = setTimeout(connect, reconnectDelay);
      reconnectDelay += RECONNECT_STEP_MS;
    };

    const connect = () => {
      if (cancelled) return;

      ws = new WebSocket(buildWebSocketURL());

      ws.onopen = () => {
        reconnectDelay = RECONNECT_STEP_MS;
        if (hasConnectedRef.current) {
          requestChannels();
        }
        hasConnectedRef.current = true;
      };

      ws.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch (err) {
          return;
        }
        if (!data || !data.type) return;

        if (data.type === 'new_message' && data.payload) {
          newMessageReceived(data.payload);
        } else if (data.type === 'message_read' && data.payload) {
          messageReadReceived(data.payload);
        } else if (data.type === 'message_edited' && data.payload) {
          messageEditedReceived(data.payload);
        } else if (data.type === 'message_deleted' && data.payload) {
          messageDeletedReceived(data.payload);
        }
      };

      ws.onerror = () => {
        console.warn('[Chat WS] Connection error');
      };

      ws.onclose = () => {
        ws = null;
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (ws) {
        ws.onclose = null;
        ws.close();
        ws = null;
      }
    };
  }, [enabled, loggedIn, sessionID]);
};
